import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, MapTrifold, Trash, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Boards() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const load = () =>
    api.get("/boards").then((r) => { setBoards(r.data); setLoading(false); }).catch(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/boards", { name, description });
      setName(""); setDescription(""); setOpen(false);
      toast.success("Board created");
      load();
    } catch {
      toast.error("Failed to create board");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/boards/${id}`);
      toast.success("Board deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="pt-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A6E7C]">Your collections</span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2">Trip Boards</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#2E523A] hover:bg-[#26442f] h-12 px-6" data-testid="new-board-btn">
                <Plus size={20} weight="bold" /> New board
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Create a trip board</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <Input placeholder="Board name (e.g. Summer in Italy)" value={name} onChange={(e) => setName(e.target.value)} data-testid="board-name-input" />
                <Textarea placeholder="What's this trip about?" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="board-description-input" />
              </div>
              <DialogFooter>
                <Button onClick={create} className="rounded-full bg-[#2E523A] hover:bg-[#26442f] w-full" data-testid="create-board-btn">Create board</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-muted-foreground py-16 text-center">Loading…</p>
        ) : boards.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#D2D5CD] rounded-2xl" data-testid="empty-boards">
            <MapTrifold size={48} weight="duotone" className="text-[#4A6E7C] mx-auto mb-4" />
            <h3 className="font-display text-2xl font-bold">No boards yet</h3>
            <p className="text-[#63675E] mt-2 mb-6">Save destinations from Explore to start planning.</p>
            <Link to="/explore"><Button variant="outline" className="rounded-full">Browse destinations</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="boards-grid">
            {boards.map((b, i) => (
              <div key={b.id} className="group border border-[#D2D5CD] rounded-2xl bg-white p-6 hover:border-[#2E523A]/40 transition-colors duration-200 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`board-item-${b.id}`}>
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-xl bg-[#EAEBE6] flex items-center justify-center mb-4">
                    <MapTrifold size={26} weight="duotone" className="text-[#2E523A]" />
                  </div>
                  <button onClick={() => remove(b.id)} className="p-2 rounded-full text-[#63675E] hover:text-destructive hover:bg-[#EAEBE6] transition-colors duration-200" data-testid={`delete-board-${b.id}`}>
                    <Trash size={18} />
                  </button>
                </div>
                <h3 className="font-display text-xl font-bold">{b.name}</h3>
                {b.description && <p className="text-sm text-[#63675E] mt-1 line-clamp-2">{b.description}</p>}
                <div className="flex items-center justify-between mt-5">
                  <span className="text-sm text-[#63675E]">{b.listing_ids?.length || 0} saved</span>
                  <Link to={`/boards/${b.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#2E523A] group-hover:gap-2 transition-all duration-200" data-testid={`open-board-${b.id}`}>
                    Open <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
